package com.hyperether.goodjob.scenes.jobs

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.Divider
import androidx.compose.material.Text
import androidx.compose.material3.DrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.navigation.NavHostController
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.Sp
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.navigation.*
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.cancel
import com.hyperether.goodjob.resources.check_soon
import com.hyperether.goodjob.resources.edit
import com.hyperether.goodjob.resources.error
import com.hyperether.goodjob.resources.job
import com.hyperether.goodjob.resources.job_deleted
import com.hyperether.goodjob.resources.jobs
import com.hyperether.goodjob.resources.no_tasks_assigned
import com.hyperether.goodjob.resources.no_tasks_created
import com.hyperether.goodjob.resources.ok
import com.hyperether.goodjob.resources.success
import com.hyperether.goodjob.resources.total
import com.hyperether.goodjob.scenes.components.ErrorDialog
import com.hyperether.goodjob.scenes.components.FilterButton
import com.hyperether.goodjob.scenes.components.MobileHeaderSearchAdd
import com.hyperether.goodjob.scenes.components.PopUpDialog
import com.hyperether.goodjob.scenes.components.SearchField
import com.hyperether.goodjob.scenes.jobs.components.FilterJobsDialog
import com.hyperether.goodjob.scenes.jobs.components.JobsList
import com.hyperether.goodjob.scenes.register.FullScreenProgressBar
import com.hyperether.goodjob.theme.TextColor
import com.hyperether.goodjob.util.Constants.DEFAULT_JOB_ID
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.goodjob.util.JobStatusMap
import com.hyperether.resources.stringResource

@Composable
fun JobsScreen(
    isMobileView: Boolean,
    viewModel: JobListViewModel,
    navHostController: NavHostController,
    drawerState: DrawerState,
    prefsManager: PrefsManager
) {

    viewModel.jobStatusMap = JobStatusMap()
    val searchText = remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        viewModel.showEditPopUp.value = false
        viewModel.isInEditMode.value = false
        FilterDialogStateManager.setDialogShowing(false)
        if (getPlatformChecker().isMobile())
            viewModel.loadRemoteData()
    }

    val errorMessage = remember { mutableStateOf("") }
    val showErrorDialog = remember { mutableStateOf(false) }
    val isInSearchMode = remember { mutableStateOf(false) }

    if (isMobileView) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column {
                MobileHeaderSearchAdd(
                    title = stringResource(Res.string.jobs),
                    drawerState = drawerState,
                    scope = rememberCoroutineScope(),
                    add = {
                        navHostController.navigate(AddNewJob(DEFAULT_JOB_ID))
                    },
                    search = {
                        isInSearchMode.value = true
                    },
                    onCancelSearch = {
                        isInSearchMode.value = false
                        searchText.value = ""
                    },
                    user = prefsManager.getUser()
                )
                if (isInSearchMode.value) {
                    Spacer(modifier = Modifier.height(Dp(15f)))
                    SearchField(searchText)
                } else {
                    if (viewModel.jobList.isNotEmpty()) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = Dp(16f), vertical = Dp(10f))
                        ) {
                            if (prefsManager.getUser()?.role != Role.employee.name) {
                                if (!viewModel.isInEditMode.value) {
                                    viewModel.showEditPopUp.value = false
                                    DisableSelection {
                                        Text(
                                            text = stringResource(Res.string.edit),
                                            fontSize = Sp(14f),
                                            color = TextColor,
                                            modifier = Modifier
                                                .clickable {
                                                    viewModel.isInEditMode.value = true
                                                    viewModel.showEditPopUp.value = true
                                                }
                                                .weight(1f),
                                            textAlign = TextAlign.Start
                                        )
                                    }
                                } else {
                                    DisableSelection {
                                        Text(
                                            text = stringResource(Res.string.cancel),
                                            fontSize = Sp(14f),
                                            color = TextColor,
                                            modifier = Modifier
                                                .clickable {
                                                    viewModel.isInEditMode.value = false
                                                    viewModel.showEditPopUp.value = false
                                                    viewModel.selectedJobs.clear()
                                                }
                                                .weight(1f),
                                            textAlign = TextAlign.Start
                                        )
                                    }
                                }
                            }

                            if (!viewModel.isInEditMode.value) {
                                val leadingText =
                                    if (viewModel.filteredJobs.size == 1) stringResource(Res.string.job) else stringResource(
                                        Res.string.jobs
                                    )
                                Text(
                                    text = "${stringResource(Res.string.total)}: ${viewModel.filteredJobs.size} $leadingText",
                                    fontSize = Sp(14f),
                                    color = Color.LightGray,
                                    maxLines = 1,
                                    fontStyle = FontStyle.Normal,
                                    textAlign = TextAlign.Center
                                )
                            }

                            Row(
                                horizontalArrangement = Arrangement.End,
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.weight(1f)
                            ) {
                                FilterButton()
                            }
                        }
                    }
                    Divider(
                        modifier = Modifier.height(Dp(2f))
                            .background(Color.LightGray.copy(alpha = 0.1f))
                    )
                }
                if (viewModel.isEmployee() && viewModel.filteredJobs.size == 0) {
                    androidx.compose.material3.Text(
                        text = stringResource(Res.string.no_tasks_created),
                        color = Color.Black,
                        fontSize = Sp(24f),
                        textAlign = TextAlign.Center,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.fillMaxWidth()
                            .padding(top = Dp(60f), start = Dp(16f), end = Dp(16f))
                    )
                    androidx.compose.material3.Text(
                        text = stringResource(Res.string.check_soon),
                        color = Color.Gray,
                        textAlign = TextAlign.Center,
                        fontSize = Sp(16f),
                        modifier = Modifier.padding(top = Dp(20f), start = Dp(16f), end = Dp(16f))
                    )
                } else if (!viewModel.isEmployee() && viewModel.filteredJobs.size == 0) {
                    viewModel.isInEditMode.value = false
                    viewModel.showEditPopUp.value = false
                    androidx.compose.material3.Text(
                        text = stringResource(Res.string.no_tasks_created),
                        color = Color.Black,
                        fontSize = Sp(24f),
                        textAlign = TextAlign.Center,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.fillMaxWidth()
                            .padding(top = Dp(60f), start = Dp(16f), end = Dp(16f))
                    )
                    androidx.compose.material3.Text(
                        text = stringResource(Res.string.no_tasks_assigned),
                        color = Color.Gray,
                        textAlign = TextAlign.Center,
                        fontSize = Sp(16f),
                        modifier = Modifier.padding(top = Dp(20f), start = Dp(16f), end = Dp(16f))
                    )
                } else {
                    val jobs = viewModel.searchAndSortJobs(searchText.value)
                    if (jobs.isEmpty()) {
                        viewModel.isInEditMode.value = false
                        viewModel.showEditPopUp.value = false
                    }
                    JobsList(
                        viewModel.sitesMap,
                        jobs,
                        viewModel.selectedJobs,
                        isInEditMode = viewModel.isInEditMode.value,
                        navHostController
                    )
                }
            }
            if (viewModel.showEditPopUp.value) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = 0.05f))
                )

                Box(
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                ) {
                    EditJobsDialog(
                        showDialog = viewModel.showEditPopUp.value,
                        jobs = viewModel.selectedJobs,
                        onDelete = {
                            viewModel.showLoader.value = true
                            viewModel.deleteSelectedJobs(
                                onAllDeleted = {
                                    viewModel.showLoader.value = false
                                    viewModel.showPopUpSuccess.value = true
                                },
                                onError = {
                                    viewModel.showLoader.value = false
                                    viewModel.showPopUpError.value = true
                                }
                            )
                            viewModel.showEditPopUp.value = false
                            viewModel.isInEditMode.value = false
                        },
                        onExportList = {},
                        onImportList = {},
                        isEmployee = viewModel.isEmployee()
                    )
                }
            }
            if (viewModel.showPopUpSuccess.value) {
                PopUpDialog(
                    onDismiss = { viewModel.showPopUpSuccess.value = false },
                    title = stringResource(Res.string.success).uppercase(),
                    message = stringResource(Res.string.job_deleted),
                    onPositiveBtnClicked = { viewModel.showPopUpSuccess.value = false },
                    positiveBtnText = stringResource(Res.string.ok)
                )
            }
            if (viewModel.showPopUpError.value) {
                ErrorDialog(
                    onDismiss = { viewModel.showPopUpError.value = false },
                    title = stringResource(Res.string.error).uppercase(),
                    message = viewModel.errorText.value
                )
            }
            if (viewModel.showLoader.value) {
                FullScreenProgressBar()
            }
            if (showErrorDialog.value) {
                ErrorDialog(
                    onDismiss = { showErrorDialog.value = false },
                    title = "",
                    message = errorMessage.value
                )
            }

            val userTeams = viewModel.getUser()?.teams
            if (FilterDialogStateManager.dialogShowing.value) {
                FilterJobsDialog(
                    viewModel.getFilterMap(),
                    onDismiss = {
                        FilterDialogStateManager.setDialogShowing(false)
                    },
                    onApply = { map ->
                        FilterDialogStateManager.setDialogShowing(false)
                        viewModel.applyFilter(map)
                        viewModel.filterJobs()
                    },
                    isMobileView = isMobileView,
                    isEmployee = viewModel.isEmployee(),
                    userTeams = userTeams
                )
            }
        }
    } else {
        SelectionContainer {
            JobsWeb(
                isMobileView = isMobileView,
                navController = navHostController,
                viewModel = viewModel
            )
        }
    }
}
